import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IconButtonSize } from './button-variants';
import { resolveIconButtonClassName } from './iconButtonClassNameFromProps';
import type { IconButtonNavbarHitArea, IconButtonTone } from './iconButtonClassNameFromProps';

export type { IconButtonSize };
export type { IconButtonNavbarHitArea, IconButtonTone };

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  ref?: React.Ref<HTMLButtonElement>;
  children: React.ReactNode;
  /** Ghost icon size (ignored when `navbar` is true — use `navbarHitArea`). */
  size?: IconButtonSize;
  /** Dark navbar: flat hover, white focus ring. */
  navbar?: boolean;
  /** Navbar tap target; ignored when `navbar` is false. */
  navbarHitArea?: IconButtonNavbarHitArea;
  /** Danger styling for destructive row actions. */
  destructive?: boolean;
  loading?: boolean;
  /** Text / icon colors and common layout presets. */
  tone?: IconButtonTone;
  /** Data table row actions: mobile 40×40, desktop 28×28. */
  tableCell?: boolean;
  /** Kebab / more menu trigger (use with `tone="menu"`). */
  menuOpen?: boolean;
  /** Search field clear, etc.: no border/background paint. */
  surface?: 'default' | 'embedded';
  /** Icon + visible label row (e.g. favorite in menus). */
  expandForLabel?: boolean;
  /** Notification card “mark read” corner control. */
  pinned?: 'top-right';
}

const spinnerSize: Record<IconButtonSize, string> = {
  'icon-sm': 'h-3.5 w-3.5',
  icon: 'h-4 w-4',
  'icon-lg': 'h-5 w-5',
  'icon-touch': 'h-5 w-5 md:h-4 md:w-4',
  'mobile-action': 'h-5 w-5 md:h-4 md:w-4',
};

function IconButton({
  className,
  size = 'icon',
  navbar = false,
  navbarHitArea,
  destructive = false,
  loading = false,
  disabled,
  tone = 'default',
  tableCell = false,
  menuOpen = false,
  surface = 'default',
  expandForLabel = false,
  pinned,
  children,
  ref,
  ...props
}: IconButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      type='button'
      ref={ref}
      disabled={isDisabled}
      className={resolveIconButtonClassName({
        className,
        size,
        navbar,
        navbarHitArea,
        destructive,
        tone,
        tableCell,
        menuOpen,
        surface,
        expandForLabel,
        pinned,
      })}
      {...props}
    >
      {loading ? <Loader2 className={cn('animate-spin shrink-0', spinnerSize[size])} aria-hidden /> : children}
    </button>
  );
}

export { IconButton };
